/**
 * THE OFFICE'S NUMBER INSIDE A SENTENCE, as a link that calls it, with the punctuation after it.
 *
 * - IT IS DRAWN AS `INLINE_LINK` (`buttons.ts`): still a box at least 48 tall to take a thumb, laid
 *   out as a plain word, so its line is no taller than the rest of the sentence.
 * - THE FULL STOP AFTER IT STAYS ON ITS LINE. The link is laid out as one box, and Chrome may break
 *   a line between such a box and the punctuation that follows it, which left a full stop alone at
 *   the start of the next line in a CI shot of sign-in's code step. The number and `after` sit in
 *   one span that does not wrap, so they go to the next line together; the space before the number
 *   is outside the span, so the sentence still breaks there. The link's box and words are as they
 *   were. `api/test/links.test.ts` fails the build on a link in a sentence followed by punctuation
 *   outside such a span, and `e2e/drawn.spec.ts` measures it at every width its sentence takes.
 */
import { PHONE_HREF, PHONE_SHOWN } from '../contact.ts';
import { INLINE_LINK } from './buttons.ts';

export function Phone({ after }: { readonly after?: '.' | ',' }) {
  return (
    <span className="whitespace-nowrap">
      <a href={PHONE_HREF} className={INLINE_LINK}>
        {PHONE_SHOWN}
      </a>
      {after}
    </span>
  );
}

/**
 * A SENTENCE OF THE CONTRACT'S THAT NAMES THE OFFICE'S NUMBER, word for word, with the number drawn
 * as `Phone` draws it, a link that calls it, the full stop or comma after it kept on its line. A
 * sentence that does not name the number is drawn as it is.
 */
export function WithPhone({ words }: { readonly words: string }) {
  const at = words.indexOf(PHONE_SHOWN);
  if (at === -1) return <>{words}</>;
  const rest = words.slice(at + PHONE_SHOWN.length);
  const after = rest.startsWith('.') ? '.' : rest.startsWith(',') ? ',' : undefined;
  // What follows the number and its stop, after the space the sentence has there.
  const tail = (after === undefined ? rest : rest.slice(1)).trimStart();
  return (
    <>
      {words.slice(0, at)}
      <Phone after={after} /> {tail}
    </>
  );
}
